import React, { useState } from "react";
import {
  Drawer,
  Typography,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { CheckCircle as CheckIcon, Close as CloseIcon, Warning as WarningIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

function PlaywrightTypeScriptSuccessPopup({ 
  open, 
  handleClose, 
  onDownload, 
  onPushToGitHub,
  isDownloading = false,
  projectGenerationFailed = false
}) {
  const navigate = useNavigate();
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [hasPushedToGit, setHasPushedToGit] = useState(false);

  // Function to navigate to the test automation dashboard
  const handleNavigate = () => {
    if (!projectGenerationFailed && !hasDownloaded && !hasPushedToGit) {
      setShowWarningDialog(true);
      return;
    }
    navigate("/dashboard/test-automation");
  };

  // Function to handle close button click
  const handleCloseClick = () => {
    if (!projectGenerationFailed && !hasDownloaded && !hasPushedToGit) {
      setShowWarningDialog(true);
      return;
    }
    handleClose();
  };

  // Function to handle download
  const handleDownloadClick = () => {
    setHasDownloaded(true);
    onDownload();
  };

  // Function to handle push to GitHub
  const handlePushToGitHubClick = () => {
    setHasPushedToGit(true);
    onPushToGitHub();
  };

  // Function to force close (when user confirms they want to lose changes)
  const handleForceClose = () => {
    setShowWarningDialog(false);
    handleClose();
  };

  // Function to cancel close and go back
  const handleCancelClose = () => {
    setShowWarningDialog(false);
  };

  return (
    <>
      {/* Warning Dialog */}
      <Dialog
        open={showWarningDialog}
        onClose={handleCancelClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon sx={{ color: "#ff9800" }} />
          <Typography variant="h6" sx={{ fontFamily: "Manrope", fontWeight: 600 }}>
            Unsaved Changes
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "Manrope", fontSize: "14px", color: "#666" }}>
            Please download or push your changes to GitHub, otherwise your generated project will be lost and you'll need to regenerate it.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 1, p: 2 }}>
          <Button
            onClick={handleCancelClose}
            variant="outlined"
            sx={{
              borderColor: "#0094FB",
              color: "#0094FB",
              textTransform: "none",
              fontFamily: "Manrope",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 16px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleForceClose}
            variant="contained"
            sx={{
              backgroundColor: "#ff9800",
              color: "white",
              textTransform: "none",
              fontFamily: "Manrope",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 16px",
              "&:hover": {
                backgroundColor: "#f57c00",
              },
            }}
          >
            Close Anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseClick}
        slotProps={{
          paper: {
            sx: {
              width: {
                xs: "90vw", // Mobile
                sm: "70vw", // Tablet
                md: "50vw", // Small laptops
                lg: "40vw", // Desktop
              },
              maxWidth: "600px",
            },
          },
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            height: "100%",
            gap: "24px",
          }}
        >
        {/* Close button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            onClick={handleCloseClick}
            sx={{
              color: "#666",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Success/Error Icon and Message */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            {projectGenerationFailed ? (
              <>
                <WarningIcon 
                  sx={{ 
                    color: "#f44336", 
                    fontSize: "48px", 
                    mb: 2
                  }} 
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: "18px",
                    fontFamily: "Manrope",
                    fontWeight: "bold",
                    color: "#000000",
                    mb: 2,
                    textAlign: "center",
                  }}
                >
                  Project generation failed
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "14px",
                    fontFamily: "Manrope",
                    color: "#666666",
                    textAlign: "center",
                  }}
                >
                  There was an error generating your project. Please try again or contact support.
                </Typography>
              </>
            ) : (
              <>
                <CheckIcon 
                  sx={{ 
                    color: "#4caf50", 
                    fontSize: "48px", 
                    mb: 2
                  }} 
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: "18px",
                    fontFamily: "Manrope",
                    fontWeight: "bold",
                    color: "#000000",
                    mb: 2,
                    textAlign: "center",
                  }}
                >
                  Your Playwright project is successfully generated
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "14px",
                    fontFamily: "Manrope",
                    color: "#666666",
                    textAlign: "center",
                  }}
                >
                  You may now proceed to tweak the script in downloads folder and execute
                </Typography>
              </>
            )}
          </Box>

        </Box>

        {/* Footer Buttons */}
        <Box sx={{ display: "flex", gap: "24px", alignItems: "center" }}>
          {/* OK Button */}
          <Button
            onClick={handleNavigate}
            variant="outlined"
            sx={{
              width: "80px",
              height: "40px",
              opacity: 1,
              borderRadius: "8px",
              borderWidth: "2px",
              paddingTop: "8px",
              paddingRight: "16px",
              paddingBottom: "8px",
              paddingLeft: "16px",
              borderColor: "#0094FB",
              color: "#0094FB",
              textTransform: "none",
              fontFamily: "Manrope",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#0094FB",
                backgroundColor: "rgba(0, 148, 251, 0.04)",
              },
            }}
          >
            OK
          </Button>

          {/* Download Button - Disabled when project generation failed */}
          <Button
            onClick={handleDownloadClick}
            disabled={isDownloading || projectGenerationFailed}
            variant="outlined"
            sx={{
              width: "160px",
              height: "40px",
              opacity: projectGenerationFailed ? 0.5 : 1,
              borderRadius: "8px",
              borderWidth: "2px",
              paddingTop: "8px",
              paddingRight: "16px",
              paddingBottom: "8px",
              paddingLeft: "16px",
              borderColor: projectGenerationFailed ? "#ccc" : "#0094FB",
              color: projectGenerationFailed ? "#666" : "#0094FB",
              textTransform: "none",
              fontFamily: "Manrope",
              fontWeight: 500,
              "&:hover": {
                borderColor: projectGenerationFailed ? "#ccc" : "#0094FB",
                backgroundColor: projectGenerationFailed ? "transparent" : "rgba(0, 148, 251, 0.04)",
              },
              "&:disabled": {
                borderColor: "#ccc",
                color: "#666",
              },
            }}
          >
            {isDownloading ? "Downloading..." : "Download"}
          </Button>

          {/* Push to GitHub Button - Disabled when project generation failed */}
          <Button
            onClick={handlePushToGitHubClick}
            disabled={projectGenerationFailed}
            variant="contained"
            sx={{
              width: "180px",
              height: "40px",
              opacity: projectGenerationFailed ? 0.5 : 1,
              borderRadius: "8px",
              borderWidth: "2px",
              paddingTop: "8px",
              paddingRight: "16px",
              paddingBottom: "8px",
              paddingLeft: "16px",
              backgroundColor: projectGenerationFailed ? "#ccc" : "#0094FB",
              color: "white",
              textTransform: "none",
              fontFamily: "Manrope",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: projectGenerationFailed ? "#ccc" : "#0078D4",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
                color: "#666",
              },
            }}
          >
            Push to GitHub →
          </Button>
        </Box>
      </Box>
    </Drawer>
    </>
  );
}

export default PlaywrightTypeScriptSuccessPopup;
